// High-intent keyword landing pages. Each one targets a specific Google query
// where buyer intent is strong. Pages prerender as static HTML at build time
// so meta + content + FAQ ship in the initial HTML for crawlers.

export const KEYWORD_PAGES = {
  'best-crypto-trading-bot': {
    slug: 'best-crypto-trading-bot',
    seoTitle: 'Best Crypto Trading Bot in 2026',
    h1: 'The best crypto trading bot for 2026 — DEX + CEX + copy trading in one',
    metaDescription:
      'Looking for the best crypto trading bot? haythix combines DEX + CEX automation, real copy trading, AI signals, and tokenomics tools across 15 chains and 15 exchanges. 14-day free trial.',
    intro:
      'The "best crypto trading bot" depends on what you actually want to do. Most platforms force you to pick between CEX automation OR DEX trading OR copy trading. haythix is the single platform that does all three with one login, one bot engine, and one subscription.',
    pillarPoints: [
      {
        title: 'Unified DEX + CEX bot engine',
        body: 'Same Grid, DCA, Smart Trade, and Conditional Bot logic runs against 15 CEX integrations (Coinbase, Kraken, Bybit, Binance US, OKX, KuCoin, and more) AND 15 DEX chains (Ethereum, Base, Polygon, Arbitrum, BSC, Solana, Avalanche, Optimism, plus 7 others). No platform-switching to chase opportunities.',
      },
      {
        title: 'Real copy trading with auto-execution',
        body: 'Not advisory signals — actual trade replication. Pick a master trader, set your allocation, and the bot executes the same trades on your exchange or wallet the moment the master fires. 20% drawdown stop-out built in, 10% master profit share, 1% platform fee.',
      },
      {
        title: 'Cross-chain arbitrage detector',
        body: 'Most "arbitrage bots" find spreads between two CEXes. haythix detects spreads across DEXes on different chains (Base vs Polygon, Arbitrum vs Optimism) and computes net profit after gas — the highest-edge category in 2026.',
      },
      {
        title: 'AI signals with verifiable methodology',
        body: 'MediTrade AI generates entry/exit signals you can stack with rule-based bots. Every signal has a confidence band — you decide whether to auto-execute or use as a heads-up. No black-box "trust us" output.',
      },
      {
        title: 'Tokenomics + token launch tools',
        body: 'If you trade AND advise on token launches, haythix covers both sides: Tokenomics Studio, Launch Volume Forecaster, Launch Roadmap Planner, CEX Listing Readiness Scorecard, Token Launch Scanner, and Wallet Profiler.',
      },
      {
        title: 'Honest pricing',
        body: 'Starter $10/mo, Trader $49.99/mo, Pro $75.99/mo, Elite $149.99/mo. All tiers include DEX + CEX trading. 14-day free trial with every feature. Pay in card or USDC.',
      },
    ],
    faqs: [
      {
        q: 'Which crypto trading bot is best for beginners?',
        a: 'haythix Starter ($10/mo) gives you the full Smart Trade + DCA + Paper Trading sandbox with 18 prebuilt strategy templates. Beginners should run a strategy in paper trading for 1-2 weeks before going live with small size ($10-50). The Library has 102 step-by-step guides explaining every workflow.',
      },
      {
        q: 'Which crypto bot supports both DEX and CEX trading?',
        a: 'haythix is the only major bot platform that unifies both. Most competitors (3Commas, Cryptohopper, Bitsgap, Coinrule, Pionex) are CEX-only. DexTools and DexScreener are DEX-only and read-only (no execution).',
      },
      {
        q: 'Is a crypto trading bot worth it in 2026?',
        a: 'Bots are worth it when your strategy is rule-based and you can not watch charts 24/7. Grid bots, DCA, and TP/SL automation are mechanical work that bots do better than humans. Discretionary "feel" trading is NOT a good fit for bots.',
      },
      {
        q: 'Do trading bots actually make money?',
        a: 'A bot does not invent edge — it enforces a strategy. If your strategy has positive expectancy backtested over real historical data, a bot maintains that edge with discipline humans can not. If the strategy is poor, the bot loses money efficiently. Backtest first.',
      },
      {
        q: 'How much does a crypto trading bot cost?',
        a: 'haythix: $10-149.99 /mo. 3Commas: $22-66 /mo (CEX-only). Cryptohopper: $19.99-129.99 /mo (CEX-only). Bitsgap: $24-110 /mo (CEX-only). Coinrule: $29.99-449 /mo (CEX-only). Pionex: free but locks you to their exchange.',
      },
      {
        q: 'Is haythix custodial?',
        a: 'No. CEX trading uses your exchange API keys (encrypted at rest, never withdraw permission). DEX trading uses your wallet — you sign every transaction. haythix never holds funds.',
      },
    ],
    relatedComparisons: ['3commas', 'cryptohopper', 'bitsgap', 'pionex'],
  },

  'dex-trading-bot': {
    slug: 'dex-trading-bot',
    seoTitle: 'DEX Trading Bot — multi-chain automation',
    h1: 'DEX Trading Bot — automated DEX trading on 15 chains',
    metaDescription:
      'Run a real DEX trading bot on Ethereum, Base, Polygon, Arbitrum, BSC, Solana, and 9 other chains. Grid, DCA, Smart Trade, Limit Orders, Stop Orders, TradingView webhooks, Algo Orders, Conditional Bot, Arbitrage. MEV-protected execution.',
    intro:
      'Most "trading bots" can not touch DEXes — they are CEX-only API key wrappers. haythix runs full bot automation on 15 chains with MEV-protected execution, real on-chain order types, and the same engines you use for CEX.',
    pillarPoints: [
      {
        title: '15-chain DEX coverage',
        body: 'Ethereum, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom, zkSync Era, Mantle, Linea, Scroll, Blast. Bot strategies run on each — no need to bridge to a "supported" chain.',
      },
      {
        title: '9 DEX bot types',
        body: 'Smart Trade (TP/SL), Grid Bot, DCA Safety Bot, Limit Orders, Stop Orders, TradingView Webhooks, Algo Orders (TWAP/VWAP/Iceberg), Conditional Bot (regime-switching), Cross-DEX Arbitrage.',
      },
      {
        title: 'MEV protection by default',
        body: 'Orders route through CoW Protocol and other MEV-protected solver networks. No frontrunning, no sandwich attacks. Stop orders use Chainlink price feeds (oracle-resistant to flash manipulation).',
      },
      {
        title: 'No custody — your wallet signs',
        body: 'haythix never holds keys. You connect MetaMask, Rabby, Coinbase Wallet, or any WalletConnect-compatible wallet. Pre-signed orders for limit/stop/algo strategies use revocable approvals you control.',
      },
      {
        title: 'Token Launch Scanner before you trade',
        body: 'Built-in rug-risk analyzer scans any token contract for: bot volume detection, holder concentration, mint authority, liquidity quality, blacklist functions. Use BEFORE a Smart Trade fires on a new token.',
      },
    ],
    faqs: [
      {
        q: 'Can a trading bot run on Uniswap?',
        a: 'Yes. haythix runs Grid Bot, DCA, Smart Trade, Limit Orders, and Algo Orders against Uniswap V3 and V2 on Ethereum, Polygon, Arbitrum, Base, and Optimism. Routing also includes Aerodrome, SushiSwap, Curve, Balancer, and PancakeSwap depending on the chain.',
      },
      {
        q: 'Are DEX trading bots safe?',
        a: 'A non-custodial DEX bot is as safe as the wallet signing the orders. haythix uses MEV-protected solver networks (CoW, Velora) for execution. Pre-signed orders use limited approvals you can revoke. Always check the Token Launch Scanner before trading new contracts.',
      },
      {
        q: 'Which is the best DEX trading bot?',
        a: 'For multi-chain coverage + execution + automation, haythix is the only platform offering 15 chains with 9 bot types. DexTools and DexScreener are analytics-only (no execution). Most "trading bots" (3Commas, Cryptohopper, Bitsgap, Pionex, Coinrule) are CEX-only.',
      },
      {
        q: 'Do I need to bridge my tokens?',
        a: 'No. haythix runs bots natively on each chain. If your USDC is on Base, the bot trades USDC on Base. If your ETH is on Arbitrum, the bot trades on Arbitrum. Same logic, native execution per chain.',
      },
      {
        q: 'What is cross-chain arbitrage?',
        a: 'A price spread for the same token (e.g., WETH/USDC) on DEXes across different chains. haythix scans for these in real-time and computes net profit after gas. Positive-net rows are tradeable; negative-net are tracking signals.',
      },
    ],
    relatedComparisons: ['dextools', 'dexscreener'],
  },

  'crypto-copy-trading': {
    slug: 'crypto-copy-trading',
    seoTitle: 'Crypto Copy Trading — auto-execute top trader trades',
    h1: 'Crypto Copy Trading — follow top traders with real auto-execution',
    metaDescription:
      'Real crypto copy trading on haythix — not advisory. Pick a master trader, set allocation, and trades execute on your exchange or wallet the moment the master fires. 20% drawdown stop-out built in. DEX + CEX masters available.',
    intro:
      'Most "copy trading" is just signal alerts — you still have to enter trades manually. haythix copy trading is genuine auto-execution: the moment a master trader fires a trade, haythix executes the same trade on your exchange (or wallet for DEX masters), sized to your allocation.',
    pillarPoints: [
      {
        title: 'Real auto-execution',
        body: 'Not advisory. Trades fire on YOUR account within seconds of the master\'s trade. No "go check the signal" gap.',
      },
      {
        title: 'DEX + CEX masters',
        body: 'Follow CEX traders (their API key trades replicate on your exchange) AND DEX traders (their wallet trades replicate on your wallet). Most platforms only support CEX copy.',
      },
      {
        title: 'Drawdown stop-out at 20%',
        body: 'Hard-coded protection. If a copied master\'s strategy hits 20% drawdown, copying auto-pauses. You never lose more than you signed up for.',
      },
      {
        title: 'Verified track records',
        body: 'Masters listed in the Top Traders directory have on-chain or API-verified history. No "screenshots from Twitter" leaderboards.',
      },
      {
        title: 'Transparent fee structure',
        body: '10% performance fee to the master (only on profits), 1% platform fee. You see every fee before subscribing to a master.',
      },
      {
        title: 'Allocation control',
        body: 'Pick how much of YOUR capital follows a master. Mirror 1:1, fractionally, or with a cap. Adjust at any time.',
      },
    ],
    faqs: [
      {
        q: 'Is crypto copy trading profitable?',
        a: 'It depends entirely on the master you copy. Even good masters have losing months. Diversify across 2-3 uncorrelated masters, use the 20% drawdown stop-out, and never copy with more than you can lose. Past performance does not guarantee future returns.',
      },
      {
        q: 'How does haythix copy trading work?',
        a: '1) Browse the Top Traders directory with verified P&L. 2) Pick a master + allocation. 3) Trades fire on your account in real-time. 4) You can pause or unsubscribe any time. 5) 20% drawdown auto-pauses.',
      },
      {
        q: 'What fees does copy trading charge?',
        a: '10% of the master\'s profit (high-water mark) + 1% platform fee. No fees on losses. Plus normal subscription tier ($10-149.99/mo) and exchange trading fees.',
      },
      {
        q: 'Can I copy trade on a DEX?',
        a: 'Yes — haythix supports DEX copy trading where masters trade on-chain and you replicate via your own wallet. Most platforms (3Commas, Bitsgap, Coinrule) do not support this.',
      },
      {
        q: 'Is copy trading safer than discretionary trading?',
        a: 'Not inherently. Copy trading inherits the risk profile of the master you pick. The 20% drawdown stop-out is your floor. Pair with risk overview / position sizing tools to size into copy positions appropriately.',
      },
    ],
    relatedComparisons: ['3commas', 'pionex'],
  },

  'crypto-grid-bot': {
    slug: 'crypto-grid-bot',
    seoTitle: 'Crypto Grid Bot — automated grid trading on DEX + CEX',
    h1: 'Grid Bot — automated grid trading across 15 CEXes and 15 DEXes',
    metaDescription:
      'Real grid bot that runs on both centralized exchanges (Coinbase, Binance US, Kraken, others) AND on-chain DEXes (Uniswap, Aerodrome, SushiSwap). Backtest before launch. MEV-protected DEX execution.',
    intro:
      'A grid bot places buy and sell orders at fixed price intervals, profiting from sideways volatility. haythix runs the same grid engine on CEX API connections AND on-chain DEX pools — most competitors only do CEX.',
    pillarPoints: [
      {
        title: 'CEX + DEX grid in one engine',
        body: 'Run the same grid logic against your Coinbase account AND against USDC/WETH on Base in the same UI. Unified position tracking across both.',
      },
      {
        title: 'Backtest with real OHLCV',
        body: 'Before going live, backtest the grid against real historical data (CCXT for CEX, on-chain price feeds for DEX). See projected fills, capital utilization, and rough P&L.',
      },
      {
        title: 'Adjustable grid params',
        body: 'Lower / upper price, number of levels, capital per level, take-profit trigger, stop-out condition. All editable mid-flight.',
      },
      {
        title: 'MEV protection on DEX grids',
        body: 'On-chain grid orders route through MEV-protected solver networks (CoW). No frontrunning, no sandwich attacks on grid fills.',
      },
    ],
    faqs: [
      {
        q: 'When does a grid bot make money?',
        a: 'Grid bots earn from price oscillating within a range. Trending markets are bad for grids — price runs through the grid in one direction and you accumulate the losing side. Backtest your range before going live.',
      },
      {
        q: 'How many grid levels should I use?',
        a: 'More levels = smaller per-fill profit + higher frequency. Fewer levels = bigger per-fill profit + lower frequency. Start with 10-20 levels on a tight range, increase as you validate the strategy.',
      },
      {
        q: 'Can I run a grid bot on Uniswap?',
        a: 'Yes — haythix Grid Bot runs against Uniswap V3 pools on Ethereum, Polygon, Arbitrum, Base, and Optimism. Also supports Aerodrome (Base), SushiSwap, Curve, Balancer, PancakeSwap.',
      },
      {
        q: 'What is the best grid bot in 2026?',
        a: 'For multi-venue (CEX + DEX) coverage: haythix. For CEX-only: Bitsgap is the historical leader. For free in-exchange: Pionex grid (locked to Pionex exchange).',
      },
    ],
    relatedComparisons: ['bitsgap', 'pionex'],
  },

  'dca-bot': {
    slug: 'dca-bot',
    seoTitle: 'DCA Bot — dollar-cost averaging for crypto',
    h1: 'DCA Bot — automated dollar-cost averaging for DEX + CEX',
    metaDescription:
      'A real DCA bot with safety orders, take-profit triggers, and drift detection. Runs on 15 CEX integrations + 15 DEX chains. Most rigorous reconciliation in retail crypto.',
    intro:
      'DCA (dollar-cost averaging) automates regular buys regardless of price. haythix DCA adds: safety orders (extra buys on drops), take-profit triggers, drift detection (alerts if your wallet balance diverges from bot expectations), and one-click reconciliation.',
    pillarPoints: [
      {
        title: 'Safety orders',
        body: 'Extra DCA tranches that fire if price drops X% from your initial entry. Lowers your cost basis on drawdowns automatically.',
      },
      {
        title: 'Take-profit triggers',
        body: 'Auto-close the entire position once total P&L hits your target %. Locks in gains.',
      },
      {
        title: 'Drift detection + reconciliation',
        body: 'Most rigorous position-integrity architecture in retail crypto. Detects if your wallet balance diverges from bot expectations (e.g., manual trades, third-party movements). One-click reconcile to resync.',
      },
      {
        title: 'CEX + DEX in one engine',
        body: 'Run DCA on Coinbase + on USDC/WETH on Base + on any of 15 chains and 15 exchanges. Same UI, same dashboard.',
      },
    ],
    faqs: [
      {
        q: 'What is a DCA bot?',
        a: 'A bot that automatically buys a fixed dollar amount at regular intervals (daily, weekly, monthly). Smooths your entry price across market cycles. Add safety orders to handle drawdowns + take-profit triggers to lock gains.',
      },
      {
        q: 'Does DCA work in a bear market?',
        a: 'DCA is best suited for accumulating during bear markets — you buy more units when prices are low. The risk is buying into a permanent decline; pair with research and your own risk tolerance.',
      },
      {
        q: 'What is the difference between DCA and grid bot?',
        a: 'DCA buys at fixed time intervals regardless of price. Grid buys/sells at fixed PRICE intervals regardless of time. DCA is for accumulation; grid is for range trading.',
      },
      {
        q: 'Can I DCA on a DEX?',
        a: 'Yes — haythix DCA runs against Uniswap, Aerodrome, SushiSwap, and other DEXes on 15 chains. Same bot, your choice of venue.',
      },
    ],
    relatedComparisons: ['3commas', 'cryptohopper'],
  },

  'cross-chain-arbitrage': {
    slug: 'cross-chain-arbitrage',
    seoTitle: 'Cross-Chain Arbitrage Bot — DEX spreads across 15 chains',
    h1: 'Cross-Chain Arbitrage — detect and capture DEX spreads across 15 chains',
    metaDescription:
      'Real-time cross-chain arbitrage detector for crypto. Scan USDC/WETH and other major pairs across Ethereum, Base, Polygon, Arbitrum, BSC, Solana — net profit after gas. MEV-protected execution.',
    intro:
      'Most "arbitrage bots" find spreads between two CEXes. The biggest edge in 2026 is between DEXes on different chains — Base vs Polygon, Arbitrum vs Optimism. haythix detects these spreads in real-time and computes net profit AFTER gas.',
    pillarPoints: [
      {
        title: 'Cross-chain spread detection',
        body: 'Scans major pairs (USDC/WETH, USDC/USDT, native tokens) across 15 chains every ~60 seconds. Each opportunity shows: source chain, target chain, price diff %, gross profit, total cost (gas both sides + slippage), net profit.',
      },
      {
        title: 'Only positive-net rows are tradeable',
        body: 'Most rows will be negative net — that\'s the market doing its job. The detector still shows them so you can see which pairs are tight vs which have edge.',
      },
      {
        title: 'MEV-protected execution',
        body: 'Trades route through CoW Protocol solver network. No frontrunning, no sandwich attacks on arb fills.',
      },
      {
        title: 'CEX-to-CEX arbitrage too',
        body: 'Same engine handles cross-CEX spreads (Coinbase vs Kraken, Binance US vs Bybit). Use both surfaces in one platform.',
      },
    ],
    faqs: [
      {
        q: 'Is crypto arbitrage still profitable in 2026?',
        a: 'Yes, but mostly for cross-chain DEX spreads. Same-chain DEX arbitrage is heavily MEV-bot dominated. CEX-to-CEX spreads exist but are tight. Cross-chain has the most edge because most retail bots cannot scan multi-chain.',
      },
      {
        q: 'How much can I make with arbitrage?',
        a: 'Realistic expectation: 0.20-0.80% per trade after gas, 1-5 fires per day on the best pairs. Capital required matters — small trades get eaten by gas. Start with $1,000+ per trade for meaningful return.',
      },
      {
        q: 'Are arbitrage bots legal?',
        a: 'Yes. Arbitrage is a legitimate trading strategy — buying low on one venue, selling high on another. Tax implications: each leg is a taxable event. haythix Tax Report handles this.',
      },
      {
        q: 'What is the risk of cross-chain arbitrage?',
        a: 'Price movement between detection and execution (slippage), gas spikes eating profit, bridge fees if you have to rebalance, smart contract risk on DEX pools. Stick to well-audited DEXes (Uniswap, Aerodrome, Curve) and major pairs (USDC/WETH).',
      },
    ],
    relatedComparisons: ['dextools', 'dexscreener'],
  },

  'automated-crypto-trading': {
    slug: 'automated-crypto-trading',
    seoTitle: 'Automated Crypto Trading — DEX + CEX in one platform',
    h1: 'Automated Crypto Trading — bots, copy trading, and AI signals across 15 chains',
    metaDescription:
      'Automated crypto trading on haythix. Smart Trade, Grid, DCA, Conditional Bot, Algo Orders, TradingView webhooks, real copy trading, AI signals. Runs on 15 CEX integrations + 15 DEX chains. 14-day free trial.',
    intro:
      'Automated crypto trading means: a strategy you defined runs without you watching charts. haythix bundles the full automation surface — entry/exit logic, position management, regime detection, signal generation — and runs it on both CEX and DEX with one login.',
    pillarPoints: [
      {
        title: 'Multiple bot types',
        body: 'Smart Trade (TP/SL), Grid Bot, DCA Safety Bot, Conditional Bot (regime-switching), TradingView Webhook automation, Algo Orders (TWAP/VWAP/Iceberg/Bracket), Arbitrage. Pick the engine that matches your strategy.',
      },
      {
        title: 'Strategy Builder + 18 templates',
        body: 'Composable rules with 50+ indicators. Backtest against real historical data before going live. 18 prebuilt templates for common strategies (RSI mean-reversion, MACD trend, Bollinger breakout, etc.).',
      },
      {
        title: 'AI signals as a layer',
        body: 'MediTrade AI generates entry/exit signals with confidence bands. Stack on top of rule-based bots: bot fires only when AI confidence > threshold.',
      },
      {
        title: 'Paper trading sandbox',
        body: 'Every strategy can run in paper mode first. Same engine, same data feeds, no real capital. Validate for 1-2 weeks before going live.',
      },
    ],
    faqs: [
      {
        q: 'Is automated crypto trading profitable?',
        a: 'Automation does not invent edge — it enforces a strategy. If your strategy is profitable in backtests over real historical data, automation maintains that edge with discipline. If the strategy loses, automation loses faster.',
      },
      {
        q: 'How do I start with automated trading?',
        a: '1) Read the haythix Library guides (102 worked examples). 2) Pick a strategy template that matches your style. 3) Backtest it against real OHLCV. 4) Run in paper trading for 1-2 weeks. 5) Go live with small size ($10-50). 6) Scale once you have 2+ weeks of live evidence.',
      },
      {
        q: 'What is the best automated crypto trading platform?',
        a: 'For DEX + CEX coverage + copy trading + AI signals + issuer tools: haythix. For CEX-only: 3Commas, Cryptohopper, Bitsgap are the established names. For free in-exchange: Pionex.',
      },
      {
        q: 'Are automated trading bots safe?',
        a: 'A bot is as safe as its key permissions. haythix CEX API keys never have withdraw permission. DEX trades use your wallet (you sign each tx or approve limited pre-signed orders). haythix never holds funds.',
      },
    ],
    relatedComparisons: ['3commas', 'cryptohopper', 'bitsgap'],
  },
};

export const KEYWORD_PAGE_SLUGS = Object.keys(KEYWORD_PAGES);
